import React from "react";
import { createPortal } from "react-dom";
import COPY from "../copy";
import { Button } from "../design_system";

export { Button, Badge, Tag, Card, StatusPill, Progress, Avatar } from "../design_system";

const ASSETS = window.__FRAMERIVER_ASSETS__ || {};
export const asset = (path) => ASSETS[path] || `/assets/${path}`;

export const CONTENT = window.__FRAMERIVER_CONTENT__ || { locale: "en", blocks: {}, river: [] };
export const LOCALE = CONTENT.locale || document.getElementById("root")?.dataset.locale || "en";
export const C = COPY[LOCALE] || COPY.en;

export const block = (key) => (CONTENT.blocks || {})[key] || null;

export const rootConfig = () => {
  const root = document.getElementById("root") || document.getElementById("site-chrome");
  return {
    accessUrl: root?.dataset.accessUrl || "/demo_requests",
    csrfToken: root?.dataset.csrf || "",
    localeUrls: JSON.parse(root?.dataset.localeUrls || "{}"),
  };
};

/* Path of the localized homepage — section anchors resolve against it so
   the shared header/footer also work on document pages (/privacy etc.) */
export const HOME = CONTENT.home_path || "/";
export const anchorHref = (anchor) => `${HOME}${anchor}`;
/* Locale-aware path: keeps the /ru | /en prefix of the current page
   (e.g. "/privacy" -> "/ru/privacy" when the RU version lives under /ru) */
export const pathHref = (path) => (HOME === "/" ? path : HOME.replace(/\/$/, "") + path);

/* ── shared bits ──────────────────────────────────────────────── */
export function Wordmark({ light, size = 22 }) {
  return (
    <span style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: size, letterSpacing: "-0.03em", color: light ? "var(--ink-text-display)" : "var(--text-display)", lineHeight: 1 }}>
      frame<span style={{ color: light ? "var(--mark-accent-ink)" : "var(--mark-accent)" }}>river</span>
    </span>
  );
}

export function Icon({ name, size = 22, color = "var(--accent)", stroke = 1.75 }) {
  return (
    <span className="fr-ico" style={{ display: "inline-flex", width: size, height: size, color, flex: "none" }} data-stroke={stroke}>
      <i data-lucide={name}></i>
    </span>
  );
}

export function Eyebrow({ children, light }) {
  return (
    <span style={{ fontFamily: "var(--font-body)", fontSize: 12.5, fontWeight: 600, letterSpacing: "var(--tracking-caps)", textTransform: "uppercase", color: light ? "var(--accent-text-ink)" : "var(--accent-text)" }}>
      {children}
    </span>
  );
}

/* Renders a text with \n as explicit line breaks. */
export function MultilineText({ text }) {
  const lines = String(text || "").split("\n");
  return lines.map((line, i) => (
    <React.Fragment key={i}>
      {line}
      {i < lines.length - 1 && <br />}
    </React.Fragment>
  ));
}

/* ── EN | RU switch — plain links to the localized URLs ───────── */
export function LangSwitch({ light }) {
  const { localeUrls } = rootConfig();
  const locales = ["en", "ru"];
  const border = light ? "1px solid rgba(250,246,238,0.25)" : "1px solid var(--border-soft)";
  return (
    <span style={{ display: "inline-flex", alignItems: "center", borderRadius: "var(--radius-pill)", border, overflow: "hidden", flex: "none" }}>
      {locales.map((loc) => {
        const active = loc === LOCALE;
        return (
          <a key={loc} href={localeUrls[loc] || (loc === "en" ? "/" : `/${loc}`)} aria-current={active ? "true" : undefined}
            style={{
              fontFamily: "var(--font-mono)", fontSize: 11.5, fontWeight: 600, letterSpacing: "0.04em",
              textTransform: "uppercase", textDecoration: "none", padding: "5px 10px", lineHeight: 1,
              color: active ? (light ? "var(--clay-9)" : "var(--text-on-accent)") : (light ? "var(--clay-2)" : "var(--text-muted)"),
              background: active ? (light ? "var(--clay-1)" : "var(--accent)") : "transparent",
            }}>
            {loc}
          </a>
        );
      })}
    </span>
  );
}

/* ── media: screenshot | gif | video, decided by content type ─── */
export function Media({ media, alt = "", style }) {
  const base = { width: "100%", height: "100%", objectFit: "cover", display: "block", ...style };
  if (!media || !media.url) {
    return (
      <div style={{ ...base, display: "flex", alignItems: "center", justifyContent: "center", background: "linear-gradient(150deg, var(--clay-8), var(--clay-9))", minHeight: 220 }}>
        <Icon name="image" size={34} color="var(--clay-6)" />
      </div>
    );
  }
  if ((media.content_type || "").startsWith("video/")) {
    return <video src={media.url} autoPlay muted loop playsInline style={base} />;
  }
  return <img src={media.url} alt={alt} loading="lazy" style={base} />;
}

/* ── fullscreen lightbox for a screenshot ─────────────────────── */
function Lightbox({ url, alt, onClose }) {
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return createPortal(
    <div onClick={onClose} role="dialog" aria-modal="true"
      style={{ position: "fixed", inset: 0, zIndex: 100, background: "rgba(20,19,16,0.88)", display: "flex", alignItems: "center", justifyContent: "center", padding: "3vh 3vw", cursor: "zoom-out" }}>
      <img src={url} alt={alt} style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", boxShadow: "0 40px 90px -30px rgba(0,0,0,0.7)" }} />
    </div>,
    document.body
  );
}

/* ── screenshot, shown exactly as exported (no frame, no backing).
   With zoom/aspect framing from the admin it becomes a large close-up
   of one region of the screen (e.g. the right half with the timeline):
   the image is scaled to `zoom` × container width and the focus point
   (focus_x/focus_y, %) is kept aligned inside the crop window.
   Images open full-size in a lightbox on click. ── */
export function Screenshot({ media, alt }) {
  const [open, setOpen] = React.useState(false);
  if (!media || !media.url) return <Media media={media} alt={alt} />;

  const zoom = Number(media.zoom || 1);
  const aspect = String(media.aspect || "");
  const cropped = zoom > 1.01 && /^\d+:\d+$/.test(aspect);
  const isVideo = (media.content_type || "").startsWith("video/");
  const lightbox = open && !isVideo && <Lightbox url={media.url} alt={alt} onClose={() => setOpen(false)} />;
  const clickable = isVideo ? {} : { onClick: () => setOpen(true), cursor: "zoom-in" };

  if (!cropped) {
    const style = { width: "100%", height: "auto", display: "block", cursor: clickable.cursor };
    return isVideo
      ? <video src={media.url} autoPlay muted loop playsInline style={style} />
      : (
        <React.Fragment>
          <img src={media.url} alt={alt} loading="lazy" style={style} onClick={clickable.onClick} />
          {lightbox}
        </React.Fragment>
      );
  }

  const [aw, ah] = aspect.split(":").map(Number);
  const fx = Math.min(100, Math.max(0, media.focus_x ?? 50));
  const fy = Math.min(100, Math.max(0, media.focus_y ?? 50));
  const inner = {
    position: "absolute",
    width: `${zoom * 100}%`,
    height: "auto",
    left: `${fx}%`,
    top: `${fy}%`,
    transform: `translate(-${fx}%, -${fy}%)`,
    display: "block",
    maxWidth: "none",
  };
  return (
    <React.Fragment>
      <div onClick={clickable.onClick} style={{ position: "relative", width: "100%", aspectRatio: `${aw} / ${ah}`, overflow: "hidden", cursor: clickable.cursor }}>
        {isVideo
          ? <video src={media.url} autoPlay muted loop playsInline style={inner} />
          : <img src={media.url} alt={alt} loading="lazy" style={inner} />}
      </div>
      {lightbox}
    </React.Fragment>
  );
}

/* ── before/after comparison with a draggable divider.
   `before` and `after` are attachment payloads; aspect comes from the
   block's crop-window setting (defaults to 16:10). Missing images render
   a stylized placeholder half, so the block works before assets exist. ── */
export function CompareSlider({ before, after, aspect = "16:10", labels }) {
  const [pos, setPos] = React.useState(56);
  const ref = React.useRef(null);
  const dragging = React.useRef(false);
  const [aw, ah] = (/^\d+:\d+$/.test(aspect) ? aspect : "16:10").split(":").map(Number);

  const moveTo = (clientX) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    setPos(Math.min(96, Math.max(4, ((clientX - rect.left) / rect.width) * 100)));
  };

  const onPointerDown = (e) => {
    // Safari starts a native image/text selection on drag without this
    e.preventDefault();
    dragging.current = true;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    moveTo(e.clientX);
  };
  const onPointerMove = (e) => { if (dragging.current) moveTo(e.clientX); };
  const endDrag = () => { dragging.current = false; };

  const noSelect = { userSelect: "none", WebkitUserSelect: "none" };
  const half = (att, side) => {
    if (att?.url) {
      return (att.content_type || "").startsWith("video/")
        ? <video src={att.url} autoPlay muted loop playsInline style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", ...noSelect }} />
        : <img src={att.url} alt="" loading="lazy" draggable={false} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", ...noSelect }} />;
    }
    const dark = side === "after";
    return (
      <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: dark ? "linear-gradient(155deg, var(--clay-8), var(--clay-9))" : "var(--clay-2)" }}>
        <Icon name={dark ? "wand-sparkles" : "pen-tool"} size={30} color={dark ? "var(--clay-6)" : "var(--clay-5)"} />
      </div>
    );
  };

  const chip = (text, style) => (
    <span style={{ position: "absolute", top: 10, zIndex: 3, fontFamily: "var(--font-mono)", fontSize: 10.5, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", padding: "3px 9px", borderRadius: "var(--radius-pill)", background: "rgba(44,43,38,0.55)", color: "var(--clay-0)", backdropFilter: "blur(4px)", pointerEvents: "none", ...style }}>{text}</span>
  );

  return (
    <div
      ref={ref}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerLeave={endDrag}
      style={{ position: "relative", width: "100%", aspectRatio: `${aw} / ${ah}`, overflow: "hidden", cursor: "ew-resize", touchAction: "none", userSelect: "none", WebkitUserSelect: "none" }}
    >
      {half(before, "before")}
      <div style={{ position: "absolute", inset: 0, clipPath: `inset(0 0 0 ${pos}%)` }}>
        {half(after, "after")}
      </div>
      {chip(labels?.[0] || "before", { left: 10 })}
      {chip(labels?.[1] || "after", { right: 10 })}
      {/* divider */}
      <div aria-hidden="true" style={{ position: "absolute", top: 0, bottom: 0, left: `${pos}%`, width: 2, marginLeft: -1, background: "rgba(250,246,238,0.9)", boxShadow: "0 0 12px rgba(20,19,16,0.35)", zIndex: 2 }}></div>
      <div aria-hidden="true" style={{ position: "absolute", top: "50%", left: `${pos}%`, transform: "translate(-50%,-50%)", zIndex: 3, width: 40, height: 40, borderRadius: "50%", background: "rgba(250,246,238,0.92)", display: "flex", alignItems: "center", justifyContent: "center", gap: 3, boxShadow: "var(--shadow-md)" }}>
        <span style={{ borderTop: "5px solid transparent", borderBottom: "5px solid transparent", borderRight: "6px solid var(--clay-7)" }}></span>
        <span style={{ borderTop: "5px solid transparent", borderBottom: "5px solid transparent", borderLeft: "6px solid var(--clay-7)" }}></span>
      </div>
    </div>
  );
}

/* ── primary registration link, styled as the brand button.
   `inverted` flips to cream-on-terracotta for warm accent panels. ── */
export function RegisterButton({ label, url, inverted }) {
  return (
    <a
      href={url || "#cta"}
      className="fr-lift"
      style={{
        display: "inline-flex", alignItems: "center", gap: 10, textDecoration: "none",
        fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 16.5,
        color: inverted ? "var(--terra-4)" : "var(--text-on-accent)",
        background: inverted ? "var(--clay-0)" : "var(--accent)",
        padding: "15px 26px", borderRadius: "var(--radius-pill)",
        boxShadow: "var(--shadow-md), var(--shadow-clay-inset)", whiteSpace: "nowrap",
      }}
    >
      {label}
      <Icon name="chevron-right" size={18} color={inverted ? "var(--terra-4)" : "var(--text-on-accent)"} />
    </a>
  );
}

/* ── OS / client logos: path data from Simple Icons (CC0), inlined so
   the page stays self-contained ── */
const OS_PATHS = {
  apple: "M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701",
  windows: "M0 3.449L9.75 2.1v9.451H0m10.949-9.602L24 0v11.4H10.949M0 12.6h9.75v9.451L0 20.699M10.949 12.6H24V24l-12.9-1.801",
  claude: "m4.7144 15.9555 4.7174-2.6471.079-.2307-.079-.1275h-.2307l-.7893-.0486-2.6956-.0729-2.3375-.0971-2.2646-.1214-.5707-.1215-.5343-.7042.0546-.3522.4797-.3218.686.0608 1.5179.1032 2.2767.1578 1.6514.0972 2.4468.255h.3886l.0546-.1579-.1336-.0971-.1032-.0972L6.973 9.8356l-2.55-1.6879-1.3356-.9714-.7225-.4918-.3643-.4614-.1578-1.0078.6557-.7225.8803.0607.2246.0607.8925.686 1.9064 1.4754 2.4893 1.8336.3643.3035.1457-.1032.0182-.0728-.164-.2733-1.3539-2.4467-1.445-2.4893-.6435-1.032-.17-.6194c-.0607-.255-.1032-.4674-.1032-.7285L6.287.1335 6.6997 0l.9957.1336.419.3642.6192 1.4147 1.0018 2.2282 1.5543 3.0296.4553.8985.2429.8318.091.255h.1579v-.1457l.1275-1.706.2368-2.0947.2307-2.6957.0789-.7589.3764-.9107.7468-.4918.5828.2793.4797.686-.0668.4433-.2853 1.8517-.5586 2.9021-.3643 1.9429h.2125l.2429-.2429.9835-1.3053 1.6514-2.0643.7286-.8196.85-.9046.5464-.4311h1.0321l.759 1.1293-.34 1.1657-1.0625 1.3478-.8804 1.1414-1.2628 1.7-.7893 1.36.0729.1093.1882-.0183 2.8535-.607 1.5421-.2794 1.8396-.3157.8318.3886.091.3946-.3278.8075-1.967.4857-2.3072.4614-3.4364.8136-.0425.0304.0486.0607 1.5482.1457.6618.0364h1.621l3.0175.2247.7892.522.4736.6376-.079.4857-1.2142.6193-1.6393-.3886-3.825-.9107-1.3113-.3279h-.1822v.1093l1.0929 1.0686 2.0035 1.8092 2.5075 2.3314.1275.5768-.3218.4554-.34-.0486-2.2039-1.6575-.85-.7468-1.9246-1.621h-.1275v.17l.4432.6496 2.3436 3.5214.1214 1.0807-.17.3521-.6071.2125-.6679-.1214-1.3721-1.9246L14.38 17.959l-1.1414-1.9428-.1397.079-.674 7.2552-.3156.3703-.7286.2793-.6071-.4614-.3218-.7468.3218-1.4753.3886-1.9246.3157-1.53.2853-1.9004.17-.6314-.0121-.0425-.1397.0182-1.4328 1.9672-2.1796 2.9446-1.7243 1.8456-.4128.164-.7164-.3704.0667-.6618.4008-.5889 2.386-3.0357 1.4389-1.882.929-1.0868-.0062-.1579h-.0546l-6.3385 4.1164-1.1293.1457-.4857-.4554.0608-.7467.2307-.2429 1.9064-1.3114Z",
  openai: "M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.872zm16.5963 3.8558L13.1038 8.364 15.1192 7.2a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.667zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z",
};

export function OsIcon({ name, size = 17, color = "currentColor" }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true" style={{ display: "block", flex: "none" }}>
      <path d={OS_PATHS[name]} fill={color} />
    </svg>
  );
}

/* ── desktop-app link next to the registration button: the release
   featured in Admin → Releases for the visitor's OS (Mac otherwise),
   or the download section while nothing is released ── */
export function DownloadAppButton() {
  const { mac, win } = CONTENT.releases || {};
  const macUrl = mac?.url || block("download")?.mac_url;
  const onWindows = typeof navigator !== "undefined" && /Windows/i.test(navigator.userAgent || "");
  const [os, url, label] = onWindows && win?.url
    ? ["windows", win.url, C.downloadFor.win]
    : macUrl ? ["apple", macUrl, C.downloadFor.mac] : [null, anchorHref("#download"), C.downloadFor.any];
  return (
    <a
      href={url}
      className="fr-lift fr-download-link"
      style={{
        display: "inline-flex", alignItems: "center", gap: 10, textDecoration: "none",
        fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 16.5, color: "var(--text-display)",
        padding: "14px 24px", borderRadius: "var(--radius-pill)", border: "1px solid var(--border-strong)",
        background: "rgb(var(--bg-app-rgb) / 0.6)", backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
        whiteSpace: "nowrap",
      }}
    >
      {os ? <OsIcon name={os} size={18} /> : <Icon name="download" size={18} color="currentColor" />}
      {label}
    </a>
  );
}

/* ── light | dark switch. Dark is the default (nothing stored); the
   pre-paint script in the layout applies a stored light | system choice,
   this keeps it in sync and follows the OS in "system". ── */
const THEME_KEY = "fr_theme";
const THEME_ICONS = {
  system: <React.Fragment><rect x="2" y="3" width="20" height="14" rx="2" /><path d="M8 21h8M12 17v4" /></React.Fragment>,
  light: <React.Fragment><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" /></React.Fragment>,
  dark: <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" />,
};

const readThemePref = () => {
  try { return localStorage.getItem(THEME_KEY) || "dark"; } catch (e) { return "dark"; }
};
const systemDark = () => !!(window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches);
const applyTheme = (pref) => {
  const dark = pref === "dark" || (pref !== "light" && systemDark());
  document.documentElement.setAttribute("data-theme", dark ? "dark" : "light");
};

export function ThemeSwitch() {
  const [pref, setPref] = React.useState(readThemePref);

  React.useEffect(() => {
    applyTheme(pref);
    if (pref !== "system" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const follow = () => applyTheme("system");
    mq.addEventListener("change", follow);
    return () => mq.removeEventListener("change", follow);
  }, [pref]);

  const choose = (next) => {
    try {
      if (next === "dark") localStorage.removeItem(THEME_KEY);
      else localStorage.setItem(THEME_KEY, next);
    } catch (e) { /* storage blocked: the choice lasts for this page */ }
    setPref(next);
  };

  return (
    <span className="fr-theme-switch" role="group" aria-label={C.theme.label}>
      {["system", "light", "dark"].map((key) => (
        <button key={key} type="button" onClick={() => choose(key)} aria-pressed={pref === key} aria-label={C.theme[key]} title={C.theme[key]}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{THEME_ICONS[key]}</svg>
        </button>
      ))}
    </span>
  );
}

/* Mounts a <video> only where the host supports range requests (real streaming);
   otherwise shows the poster frame — so previews without range support stay clean. */
export function SmartVideo({ src, poster, watchId = "formats", style }) {
  const [ok, setOk] = React.useState(false);
  const [paused, setPaused] = React.useState(false);
  const [inView, setInView] = React.useState(false);
  const videoRef = React.useRef(null);

  React.useEffect(() => {
    let alive = true;
    fetch(src, { headers: { Range: "bytes=0-2047" } })
      .then((r) => { if (alive) setOk(r.status === 206); })
      .catch(() => { if (alive) setOk(false); });
    return () => { alive = false; };
  }, [src]);

  React.useEffect(() => {
    if (!ok) return;

    const el = document.getElementById(watchId);
    if (!el) return;

    const syncInView = () => {
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      setInView(rect.top < vh * 0.75 && rect.bottom > vh * 0.2);
    };

    const scheduleSync = () => {
      syncInView();
      [120, 350, 700, 1100].forEach((ms) => setTimeout(syncInView, ms));
    };

    syncInView();
    if (window.location.hash === `#${watchId}`) scheduleSync();
    window.addEventListener("scroll", syncInView, { passive: true });
    window.addEventListener("resize", syncInView);
    window.addEventListener("hashchange", scheduleSync);

    return () => {
      window.removeEventListener("scroll", syncInView);
      window.removeEventListener("resize", syncInView);
      window.removeEventListener("hashchange", scheduleSync);
    };
  }, [ok, watchId]);

  React.useEffect(() => {
    const video = videoRef.current;
    if (!video || !ok) return;

    const syncPlayback = () => {
      if (inView && !paused) {
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    };

    syncPlayback();
    video.addEventListener("loadeddata", syncPlayback);
    return () => video.removeEventListener("loadeddata", syncPlayback);
  }, [inView, paused, ok]);

  if (!ok) return <img src={poster} alt="" style={{ ...style, objectFit: "cover" }} />;

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      <video ref={videoRef} src={src} poster={poster} muted loop playsInline style={style} />
      <button
        type="button"
        className="fr-video-toggle"
        onClick={() => setPaused((p) => !p)}
        aria-label={paused ? "Play video" : "Pause video"}
        aria-pressed={!paused}
        style={{
          position: "absolute", right: 12, top: 12, zIndex: 3,
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          width: 36, height: 36, padding: 0, border: "none",
          borderRadius: "var(--radius-pill)", background: "rgba(44,43,38,0.55)",
          backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
          cursor: "pointer", color: "var(--clay-0)",
        }}
      >
        <Icon name={paused ? "play" : "pause"} size={18} color="var(--clay-0)" />
      </button>
    </div>
  );
}
